import { useState } from "react";
import GuideShell from "@/components/GuideShell";
import { Link } from "wouter";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  cream: "oklch(0.98 0.008 85)",
  linen: "oklch(0.93 0.01 85)",
  espresso: "oklch(0.22 0.015 65)",
  terracotta: "oklch(0.32 0.08 255)",
  terracottaLight: "oklch(0.32 0.08 255 / 0.08)",
  terracottaMid: "oklch(0.32 0.08 255 / 0.22)",
  border: "oklch(0.88 0.012 85)",
  white: "oklch(1 0.004 85)",
  muted: "oklch(0.52 0.015 65)",
  body: "oklch(0.52 0.015 65)",
};

// ── Data ─────────────────────────────────────────────────────────────────────
interface QuickWin {
  num: number;
  title: string;
  when: string;
  how: string[];
  principle: string;
}

const WINS: QuickWin[] = [
  {
    num: 1,
    title: "Know When Claude Is the Right Tool",
    when: "Use this when the quality of reasoning or tone matters more than speed.",
    how: [
      "Use Claude for long-form, nuanced reasoning and high-stakes writing",
      "Use other tools for fast exploration or rapid iteration",
      "Choose based on the job, not habit",
      "Don't force one tool to do everything",
    ],
    principle: "Tool choice follows thinking needs.",
  },
  {
    num: 2,
    title: "Paste Long Documents Without Breaking Flow",
    when: "Use this when you need feedback on complex or lengthy material.",
    how: [
      "Paste the full document in one go",
      "Ask Claude to confirm it has full context",
      "Specify the type of feedback you want",
      "Stay in the same conversation",
    ],
    principle: "Continuity protects understanding.",
  },
  {
    num: 3,
    title: "Ask for Structural Feedback, Not Rewriting",
    when: "Use this when you want improvement without losing your voice.",
    how: [
      "Ask for feedback on structure, flow, or logic",
      "Explicitly say \"Do not rewrite\"",
      "Review patterns and gaps",
      "Make changes yourself",
    ],
    principle: "Editing is not authorship.",
  },
  {
    num: 4,
    title: "Use Claude for Calm, Thoughtful Iteration",
    when: "Use this when you want refinement, not more ideas.",
    how: [
      "Share the current version",
      "Ask what could be clearer or simpler",
      "Change one thing at a time",
      "Pause between iterations",
    ],
    principle: "Progress doesn't require acceleration.",
  },
  {
    num: 5,
    title: "Detect Subtle Bias in AI Outputs",
    when: "Use this when topics are sensitive or consequential.",
    how: [
      "Ask Claude to surface assumptions",
      "Ask what perspectives may be missing",
      "Read for tone as well as content",
      "Decide what aligns with your values",
    ],
    principle: "Neutral language doesn't guarantee neutral thinking.",
  },
  {
    num: 6,
    title: "Edit Without Losing Your Voice",
    when: "Use this when edits start sounding generic.",
    how: [
      "Ask Claude to preserve tone and intent",
      "Request suggestions, not replacements",
      "Accept only what still sounds like you",
      "Trust your ear",
    ],
    principle: "Your voice is not an efficiency problem.",
  },
  {
    num: 7,
    title: "Do Side-by-Side Thinking with Two AI Tools",
    when: "Use this when decisions feel complex or high impact.",
    how: [
      "Ask the same question in Claude and another tool",
      "Compare reasoning styles, not just answers",
      "Notice what surfaces differently",
      "Decide with fuller context",
    ],
    principle: "Contrast sharpens clarity.",
  },
  {
    num: 8,
    title: "Slow the Process on Purpose",
    when: "Use this when urgency distorts judgment.",
    how: [
      "Ask Claude to take a conservative approach",
      "Request fewer options, more reasoning",
      "Read without reacting",
      "Decide later",
    ],
    principle: "Speed is a choice, not a requirement.",
  },
  {
    num: 9,
    title: "Refine Ideas Instead of Expanding Them",
    when: "Use this when ideas feel bloated or unfocused.",
    how: [
      "Ask what could be removed",
      "Ask what's most essential",
      "Tighten instead of adding",
      "Stop early",
    ],
    principle: "Clarity often comes from subtraction.",
  },
  {
    num: 10,
    title: "Use Claude When the Stakes Are High",
    when: "Use this for writing, decisions, or messaging that truly matters.",
    how: [
      "Explain context and impact",
      "Ask for careful reasoning, not speed",
      "Review thoughtfully",
      "Make the final call yourself",
    ],
    principle: "High-stakes thinking deserves deliberation.",
  },
  {
    num: 11,
    title: "Customize Claude to Know You",
    when: "Use this so Claude works like a thinking partner who already understands your context.",
    how: [
      "Use the Customize button in Claude to set your name, role, and preferences",
      "Tell Claude how you like to be communicated with: tone, length, format",
      "Set things Claude should always or never do",
      "Claude will apply these across all your conversations automatically",
    ],
    principle: "A tool that knows you works harder for you.",
  },
  {
    num: 12,
    title: "Use Projects to Keep Context Alive",
    when: "Use this when you're working on something across multiple sessions.",
    how: [
      "Create a Project in Claude for ongoing work: a newsletter, a client, a topic area",
      "Add background documents so Claude always has your context",
      "Continue conversations inside the Project without re-explaining yourself",
      "Think of it as a dedicated thinking space, not just a chat window",
    ],
    principle: "Continuity is a competitive advantage.",
  },
  {
    num: 13,
    title: "Say Less. Get More.",
    when: "Use this when over-prompting is producing over-engineered answers.",
    how: [
      "Start with a simple, direct ask. Claude understands more than you might expect",
      "Add context only if the first response misses the mark",
      "Resist the urge to over-explain or hedge in your prompt",
      "Simpler prompts often produce cleaner, more usable thinking",
    ],
    principle: "Restraint in prompting produces clarity in output.",
  },
  {
    num: 14,
    title: "Let Claude Work in the Background with Cowork",
    when: "Use this when repetitive file and task management is pulling you away from higher-value work.",
    how: [
      "Cowork is Anthropic's desktop tool for automating file organization and task workflows",
      "Use it to handle the friction work so you stay in thinking mode",
      "No coding required: designed for non-technical users",
      "Pair it with Claude for a full thinking-and-doing stack",
    ],
    principle: "Automation earns you back the time to think.",
  },
];

const GROUPS = [
  { label: "Quick Wins 1 – 3", wins: WINS.slice(0, 3) },
  { label: "Quick Wins 4 – 6", wins: WINS.slice(3, 6) },
  { label: "Quick Wins 7 – 10", wins: WINS.slice(6, 10) },
  { label: "Quick Wins 11 – 14 · What's New", wins: WINS.slice(10, 14) },
];

// ── Chevron icon ──────────────────────────────────────────────────────────────
function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16" height="16" viewBox="0 0 16 16" fill="none"
      style={{
        transition: "transform 0.25s ease",
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        flexShrink: 0,
        color: C.muted,
      }}
    >
      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Win card ──────────────────────────────────────────────────────────────────
function WinCard({ win, defaultOpen = false }: { win: QuickWin; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${open ? C.terracottaMid : C.border}`,
        borderLeft: `3px solid ${open ? C.terracotta : C.terracottaMid}`,
        borderRadius: 10,
        overflow: "hidden",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        boxShadow: open
          ? "0 6px 24px oklch(0.32 0.08 255 / 0.10)"
          : hovered
          ? "0 2px 10px rgba(0,0,0,0.06)"
          : "0 1px 4px rgba(0,0,0,0.04)",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Header button */}
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          padding: "1.1rem 1.4rem",
          background: open ? C.terracottaLight : "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          transition: "background 0.2s ease",
        }}
      >
        {/* Number badge */}
        <span
          style={{
            width: 32, height: 32, minWidth: 32,
            borderRadius: "50%",
            background: open ? C.terracotta : C.terracottaLight,
            color: open ? C.white : C.terracotta,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 13, fontWeight: 700,
            transition: "background 0.2s ease, color 0.2s ease",
            flexShrink: 0,
          }}
        >
          {win.num}
        </span>
        <span
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 16, fontWeight: 600, color: C.espresso,
            flex: 1, lineHeight: 1.35,
          }}
        >
          {win.title}
        </span>
        <ChevronIcon open={open} />
      </button>

      {/* Expanded body */}
      {open && (
        <div
          style={{
            padding: "1.25rem 1.4rem 1.5rem",
            borderTop: `1px solid ${C.terracottaMid}`,
            background: C.white,
          }}
        >
          {/* When */}
          <div style={{ marginBottom: "1.1rem" }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, marginBottom: "0.35rem" }}>
              When This Helps
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.muted, fontStyle: "italic", lineHeight: 1.7 }}>
              {win.when}
            </p>
          </div>

          {/* How */}
          <div style={{ marginBottom: "1.25rem" }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, marginBottom: "0.65rem" }}>
              How
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              {win.how.map((step, i) => (
                <div key={i} style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
                  <span
                    style={{
                      width: 20, height: 20, minWidth: 20,
                      borderRadius: "50%",
                      background: C.linen,
                      border: `1px solid ${C.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                      fontSize: 10, fontWeight: 700, color: C.muted,
                      marginTop: 2, flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </span>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.7, margin: 0 }}>
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Smart Stack Principle */}
          <div
            style={{
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderLeft: `3px solid ${C.terracotta}`,
              borderRadius: 6,
              padding: "0.75rem 1rem",
            }}
          >
            <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, display: "block", marginBottom: "0.3rem" }}>
              Smart Stack Principle
            </span>
            <span style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 14, color: C.espresso, fontStyle: "italic", lineHeight: 1.5 }}>
              {win.principle}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function ClaudeQuickWinsProtected() {
  return (
    <GuideShell
      context={"Claude guide"}
      backHref={"/resources/claude"}
      backLabel={"Back to Claude"}
    >

      <main style={{ maxWidth: 720, margin: "0 auto", padding: "3.5rem 1.5rem 5rem" }}>

        {/* Hero header */}
        <div style={{ textAlign: "center", marginBottom: "3rem", paddingBottom: "2.5rem", borderBottom: `1px solid ${C.border}` }}>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 11, fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: C.terracotta,
              marginBottom: "1rem",
            }}
          >
            Your Smart Stack Quick Wins
          </p>
          <h1
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: "clamp(28px, 5vw, 40px)",
              fontWeight: 700,
              color: C.espresso,
              lineHeight: 1.2,
              marginBottom: "0.75rem",
            }}
          >
            Claude
          </h1>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 18,
              color: C.muted,
              fontStyle: "italic",
              marginBottom: "1.5rem",
              lineHeight: 1.5,
            }}
          >
            Refine without rushing. Keep your voice.
          </p>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderRadius: 20,
              padding: "0.45rem 1.1rem",
            }}
          >
            <span
              style={{
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                fontSize: 12, fontWeight: 600,
                color: C.terracotta,
                letterSpacing: "0.04em",
              }}
            >
              Slow the thinking. Strengthen the outcome.
            </span>
          </div>
        </div>

        {/* Intro card */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${C.terracotta}`,
            borderRadius: 10,
            padding: "1.75rem 2rem",
            marginBottom: "2.75rem",
          }}
        >
          <h2
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: 22, fontWeight: 700,
              color: C.espresso,
              marginBottom: "0.3rem",
            }}
          >
            The Deliberation Layer
          </h2>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 14, color: C.muted,
              fontStyle: "italic",
              marginBottom: "1.1rem",
            }}
          >
            Your AI for careful reasoning, long-form coherence, and restraint.
          </p>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 15, color: C.body,
              lineHeight: 1.8, marginBottom: "0.9rem",
            }}
          >
            Claude is most useful when you need careful reasoning, long-form coherence, and restraint. It's not the tool you open for everything. It's the one you open when clarity, care, and coherence matter most. This guide shows how to use Claude as a deliberate thinking partner, especially when tone, stakes, and nuance are high.
          </p>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 15, color: C.body,
              lineHeight: 1.8, marginBottom: "1.5rem",
            }}
          >
            These 14 Quick Wins are designed for moments when depth matters more than speed: when you want refinement, not expansion.
          </p>

          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: "1.25rem" }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, marginBottom: "0.6rem" }}>
              How to Use This Guide
            </p>
            <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 16, fontWeight: 600, color: C.espresso, marginBottom: "0.5rem" }}>
              This is not a checklist.
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.75, marginBottom: "0.75rem" }}>
              You don't need to apply everything here. Each Quick Win is designed for moments when:
            </p>
            {[
              "depth matters more than speed",
              "structure matters more than output",
              "you want refinement, not expansion",
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: "0.65rem", alignItems: "flex-start", marginBottom: "0.45rem" }}>
                <span style={{ color: C.terracotta, fontWeight: 700, fontSize: 15, flexShrink: 0, lineHeight: 1.6 }}>–</span>
                <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.7, margin: 0 }}>{item}</p>
              </div>
            ))}
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.75, marginTop: "0.85rem" }}>
              Start with the Quick Win that slows things down in the right way. Skip the rest.
            </p>
            <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 15, fontWeight: 600, color: C.espresso, marginTop: "0.6rem", fontStyle: "italic" }}>
              Thoughtfulness is a feature, not a delay.
            </p>
          </div>
        </div>

        {/* Quick Win groups */}
        {GROUPS.map((group) => (
          <div key={group.label} style={{ marginBottom: "2.75rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.1rem" }}>
              <div style={{ height: 1, flex: 1, background: C.border }} />
              <p
                style={{
                  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  fontSize: 10, letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  color: C.terracotta,
                  whiteSpace: "nowrap",
                }}
              >
                {group.label}
              </p>
              <div style={{ height: 1, flex: 1, background: C.border }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
              {group.wins.map((win, i) => (
                <WinCard
                  key={win.num}
                  win={win}
                  defaultOpen={i === 0 && group.wins[0].num === 1}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Closing note */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${C.terracotta}`,
            borderRadius: 10,
            padding: "2rem 2.25rem",
            marginBottom: "2.5rem",
          }}
        >
          <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 700, color: C.espresso, marginBottom: "0.5rem" }}>
            A Note on Deliberation
          </h2>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.muted, fontStyle: "italic", marginBottom: "1.1rem", lineHeight: 1.7 }}>
            Claude works best when you give it time, and take time yourself.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "0.5rem" }}>
            It's not the tool you open for everything.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "1.25rem" }}>
            It's the one you open when clarity, care, and coherence matter most.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "1.75rem" }}>
            {["Slow the process.", "Protect your voice.", "Let depth do its work."].map((line) => (
              <p
                key={line}
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: 16, fontWeight: 600,
                  color: C.espresso,
                  margin: 0,
                  fontStyle: "italic",
                }}
              >
                {line}
              </p>
            ))}
          </div>
          <div
            style={{
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderRadius: 8,
              padding: "1.1rem 1.4rem",
            }}
          >
            <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 15, color: C.espresso, fontStyle: "italic", lineHeight: 1.65, marginBottom: "0.6rem" }}>
              "Claude is where I go when I want thoughtful reasoning, not fast answers."
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 13, fontWeight: 700, color: C.terracotta, marginBottom: "0.1rem" }}>
              Caren Glasser
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 11, color: C.muted, letterSpacing: "0.04em" }}>
              Creator of the Smart Stack
            </p>
          </div>
        </div>

        {/* CTA: drive to sign up */}
        <div
          style={{
            background: C.espresso,
            borderRadius: 12,
            padding: "2.5rem 2.25rem",
            textAlign: "center",
            marginBottom: "2.5rem",
          }}
        >
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 11, fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: C.terracottaMid,
              marginBottom: "0.85rem",
            }}
          >
            The AI Roles Map
          </p>
          <h2
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: "clamp(22px, 4vw, 30px)",
              fontWeight: 700,
              color: C.white,
              lineHeight: 1.25,
              marginBottom: "0.9rem",
            }}
          >
            Explore the full Claude resource library
          </h2>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 15, color: "oklch(1 0.004 85 / 0.7)",
              lineHeight: 1.7,
              maxWidth: 480,
              margin: "0 auto 1.75rem",
            }}
          >
            Head back to the Claude resource hub for interactive guides, the 8 Ways to Think with Claude, and more.
          </p>
          <Link
            href="/resources/claude"
            style={{
              display: "inline-block",
              padding: "0.85rem 2rem",
              background: C.terracotta,
              color: C.white,
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 14, fontWeight: 700,
              borderRadius: 8,
              textDecoration: "none",
              letterSpacing: "0.02em",
              transition: "opacity 0.2s ease",
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) =>
              (e.currentTarget.style.opacity = "0.85")
            }
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) =>
              (e.currentTarget.style.opacity = "1")
            }
          >
            Back to Claude Resources →
          </Link>
        </div>

        {/* Footer nav */}
        <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
          <a
            href="https://claude.ai/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 13, color: C.white,
              fontWeight: 700,
              textDecoration: "none",
              padding: "0.65rem 1.3rem",
              background: C.terracotta,
              borderRadius: 8,
              letterSpacing: "0.02em",
              transition: "opacity 0.2s ease",
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) =>
              (e.currentTarget.style.opacity = "0.85")
            }
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) =>
              (e.currentTarget.style.opacity = "1")
            }
          >
            Try Claude →
          </a>
        </div>
      </main>


      <style>{`
        @media (max-width: 600px) {
          main { padding: 2rem 1rem 4rem !important; }
        }
      `}</style>
    </GuideShell>
  );
}
